import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../components/Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../loadBalancer';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const popupVariants = {
  hidden: { opacity: 0, y: -30, scale: 0.9 },
  visible: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -30, scale: 0.9 },
};

function AuditErrorPopup() {
  const [visible, setVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [viewedKeys, setViewedKeys] = useState([]);
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const popupRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const baseUrl = getRoundRobinBaseUrl();
      const res = await api.get(`${baseUrl}/audit/getRebuttalNotification`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (res.data?.data?.length > 0) {
        let parsed = [];

        res.data.data.forEach(item => {
          if (item.allocatedAndFiles) {
            item.allocatedAndFiles.forEach(file => {
              const key = `${file.FileName}-${file.AllocatedCount}`;
              parsed.push({
                text: `File: ${file.FileName} (Allocated: ${file.AllocatedCount})`,
                key,
                type: 'allocated'
              });
            });
          } else {
            const key = Object.keys(item)[0];
            parsed.push({
              text: item[key],
              key,
              type: key
            });
          }
        });

        const newKeys = parsed.map(item => item.key);
        const hasNewMessages = newKeys.some(key => !viewedKeys.includes(key));

        setMessages(parsed);

        if (hasNewMessages) {
          setVisible(true); // Auto-open if new unseen messages
        }
      }
    } catch (e) {
      console.error('Fetch failed:', e);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [accessToken]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setVisible(false);
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [visible]);

  useEffect(() => {
    let closeTimer;
    if (visible) {
      closeTimer = setTimeout(() => {
        setVisible(false);
      }, 8000); // Close after 8 seconds
    }
    return () => {
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [visible]);

  const handleClickMessage = (type, key) => {
    setViewedKeys(prev => [...new Set([...prev, key])]); // Mark as viewed
    setVisible(false);

    if (type === 'QArebuttalFyiStatusCount') {
      navigate('/QualityControl/Rebuttal/QAFyiManagerSearch');
    } else if (type === 'QArebuttalErrorsCount') {
      navigate('/QualityControl/Rebuttal/ErrorQAManagerSearch');
    } else if (type === 'allocated') {
      navigate('/Production/AllocationFilesTemplate');
    }
  };

  const unviewedCount = messages.filter(msg => !viewedKeys.includes(msg.key)).length;

  return (
    <>
      {/* Notification Icon */}
      <div
        onClick={() => {
          fetchNotifications(); // Refresh messages on click
          setVisible(prev => !prev);
        }}
        className="fixed top-4 right-16 z-[999] cursor-pointer group"
        title="View Notifications"
      >
        <div className="relative w-9 h-9 rounded-full bg-white border-2 border-[#1A2B5C] shadow-xl flex items-center justify-center hover:scale-110 transition-transform duration-300">
          {unviewedCount > 0 && (
            <>
              <span className="absolute animate-ping h-4 w-4 bg-[#EE1D23] rounded-full top-0 right-0" />
              <span className="absolute h-3 w-3 bg-[#EE1D23] rounded-full top-0 right-0" />
              <span className="absolute -top-2 -right-2 bg-[#EE1D23] text-white text-[10px] font-bold px-1.5 py-[1px] rounded-full shadow">
                {unviewedCount}
              </span>
            </>
          )}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 text-[#1A2B5C] ${unviewedCount > 0 ? 'animate-pulse' : ''}`}
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2C10.07 2 8.54 3.53 8.54 5.46V6.07C6.38 7.19 5 9.42 5 12v3l-1.5 1.5v.5h17v-.5L19 15v-3c0-2.58-1.38-4.81-3.54-5.93v-.61C15.46 3.53 13.93 2 12 2zm0 20c1.1 0 2-.9 2-2h-4a2 2 0 002 2z" />
          </svg>
        </div>
      </div>

      {/* Popup */}
      <AnimatePresence>
        {visible && (
          <motion.div
            ref={popupRef}
            className="fixed top-20 right-6 z-[999] w-[360px] bg-white/60 border border-[#1A2B5C]/10 backdrop-blur-xl shadow-2xl rounded-2xl p-5"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={popupVariants}
            transition={{ duration: 0.35 }}
          >
            <h3 className="text-lg font-bold text-[#1A2B5C] mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2">🔔 Notification Center</span>
              <button
                onClick={() => setVisible(false)}
                className="text-[#1A2B5C] hover:text-[#EE1D23] text-xl font-bold leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </h3>

            <div className="space-y-3 overflow-y-auto max-h-[230px] pr-1 custom-scrollbar">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => handleClickMessage(msg.type, msg.key)}
                  className={`border ${viewedKeys.includes(msg.key)
                    ? 'border-gray-200 bg-white/80 text-gray-400'
                    : 'border-[#EE1D23]/30 bg-white/90 text-[#1A2B5C]'
                  } hover:bg-white hover:shadow-lg hover:scale-[1.02] transition-all rounded-xl px-4 py-3 cursor-pointer`}
                >
                  <div className="text-sm font-medium">
                    {msg.text}
                  </div>
                  <div className="text-xs mt-1 text-[#EE1D23] underline underline-offset-2">
                    Click here to view details →
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Progress Bar */}
            <motion.div
              className="h-1 mt-4 bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 8, ease: 'linear' }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default AuditErrorPopup;
